import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { usePriceHistory } from "./usePriceHistory";
import { useOperationLogs } from "./useOperationLogs";
import { cacheProducts, getCachedProducts } from "@/lib/pdv-cache";
import { getErrorMessage, logError } from "@/lib/error-messages";
import { getProducts } from "@/lib/dataGateway";

type Product = Tables<"products">;
type ProductInsert = TablesInsert<"products">;
type ProductUpdate = TablesUpdate<"products">;

export type ProductWithCategory = Product & {
  categories?: { name: string } | null;
  production_locations?: { name: string; printer_name: string } | null;
  barcode?: string | null;
  tracks_stock?: boolean | null;
  current_stock?: number | null;
  minimum_stock?: number | null;
  cost_per_unit?: number | null;
  available_pdv?: boolean | null;
  available_table?: boolean | null;
  available_tablet?: boolean | null;
  available_delivery?: boolean | null;
  is_featured?: boolean | null;
};

export function useProducts() {
  const { activeRestaurant } = useRestaurantContext();
  const [products, setProducts] = useState<ProductWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { recordPriceChange } = usePriceHistory();
  const { logOperation } = useOperationLogs();

  const fetchProducts = useCallback(async () => {
    if (!activeRestaurant) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Try to fetch from network first (via DataGateway: local server -> Supabase)
      if (navigator.onLine) {
        const { data, error, source } = await getProducts<ProductWithCategory>(activeRestaurant.id);
        if (import.meta.env.DEV && !error) {
          console.debug(`[useProducts] fonte: ${source}`);
        }

        if (error) {
          // Network error - try cache
          const cached = await getCachedProducts<ProductWithCategory>(activeRestaurant.id);
          if (cached) {
            setProducts(cached);
            toast.info("Usando produtos do cache offline");
          } else {
            toast.error("Erro ao carregar produtos");
          }
        } else {
          setProducts(data || []);
          // Cache for offline use (fire-and-forget to prevent blocking)
          cacheProducts(activeRestaurant.id, data || []).catch(() => {});
        }
      } else {
        // Offline mode - use cache
        const cached = await getCachedProducts<ProductWithCategory>(activeRestaurant.id);
        if (cached) {
          setProducts(cached);
        } else {
          toast.error("Sem conexão e sem cache disponível");
        }
      }
    } catch (error) {
      // Ensure we never get stuck in loading state
      console.error("Error fetching products:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!activeRestaurant) return null;

    const fileExt = file.name.split(".").pop();
    const fileName = `${activeRestaurant.id}/${crypto.randomUUID()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("product-images")
      .upload(fileName, file);

    if (uploadError) {
      toast.error("Erro ao fazer upload da imagem");
      console.error(uploadError);
      return null;
    }

    const { data } = supabase.storage
      .from("product-images")
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const createProduct = async (
    data: Omit<ProductInsert, "restaurant_id">,
    imageFile?: File
  ) => {
    if (!activeRestaurant) return null;

    let image_url = data.image_url;
    if (imageFile) {
      image_url = await uploadImage(imageFile);
    }

    const { data: newProduct, error } = await supabase
      .from("products")
      .insert({
        ...data,
        image_url,
        restaurant_id: activeRestaurant.id,
        // Convert empty barcode to null to avoid unique constraint issues
        barcode: data.barcode?.trim() || null,
      })
      .select()
      .single();

    if (error) {
      toast.error(getErrorMessage(error, "criar", "product"));
      logError(error, "useProducts.createProduct");
      return null;
    }

    await logOperation("create", "product", newProduct.id, {
      name: newProduct.name,
      price: newProduct.price,
      barcode: (newProduct as any).barcode,
    });

    toast.success("Produto criado com sucesso!");
    await fetchProducts();
    return newProduct;
  };

  const updateProduct = async (
    id: string,
    data: ProductUpdate,
    imageFile?: File
  ) => {
    // Find current product to check price change
    const currentProduct = products.find(p => p.id === id);
    const oldPrice = currentProduct ? Number(currentProduct.price) : 0;
    const newPrice = data.price !== undefined ? Number(data.price) : oldPrice;

    let image_url = data.image_url;
    if (imageFile) {
      image_url = await uploadImage(imageFile);
    }

    const { error } = await supabase
      .from("products")
      .update({ 
        ...data, 
        image_url,
        // Convert empty barcode to null to avoid unique constraint issues
        barcode: data.barcode !== undefined ? (data.barcode?.trim() || null) : undefined,
      })
      .eq("id", id);

    if (error) {
      toast.error(getErrorMessage(error, "atualizar", "product"));
      logError(error, "useProducts.updateProduct");
      return false;
    }

    // Record price change if price was modified
    if (oldPrice !== newPrice && currentProduct) {
      await recordPriceChange(id, oldPrice, newPrice);
    }

    await logOperation("update", "product", id, {
      name: data.name || currentProduct?.name,
      changes: {
        ...(oldPrice !== newPrice ? { price: { old: oldPrice, new: newPrice } } : {}),
        ...(data.name && data.name !== currentProduct?.name ? { name: { old: currentProduct?.name, new: data.name } } : {}),
        ...(data.is_active !== undefined && data.is_active !== currentProduct?.is_active ? { is_active: { old: currentProduct?.is_active, new: data.is_active } } : {}),
      },
    });

    toast.success("Produto atualizado!");
    await fetchProducts();
    return true;
  };

  const deleteProduct = async (id: string) => {
    const product = products.find(p => p.id === id);
    
    const { error } = await supabase
      .from("products")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error(getErrorMessage(error, "excluir", "product"));
      logError(error, "useProducts.deleteProduct");
      return false;
    }

    await logOperation("delete", "product", id, {
      name: product?.name,
      price: product?.price,
    });

    toast.success("Produto excluído!");
    await fetchProducts();
    return true;
  };

  const toggleActive = async (product: Product) => {
    return updateProduct(product.id, { is_active: !product.is_active });
  };

  return {
    products,
    isLoading,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    toggleActive,
  };
}
